import firebase from "config/firebase";
import { toast } from "react-toastify";

// ─── Loader ───────────────────────────────────────────────────────────────────
export const setBoostLoading = (val) => (dispatch) => {
  dispatch({ type: "SET_BOOST_LOADING", payload: val });
};

export const recordBoostTransaction =
  ({ athleteId, managerID, athleteName, athleteEmail, userId, fanName,
     featureType, duration, pricePerDay, totalAmount, paymentId,
     paymentMethod, opinion }) =>
  async () => {
    const db = firebase.firestore();
    const isSelfBoost = featureType === "selfBoost";

    const primaryId = isSelfBoost ? managerID : athleteId;

    const txRef      = db.collection("boost_transactions").doc(paymentId);
    const boostDocId = `${primaryId}_${featureType}`;
    const boostRef   = db.collection("boosts").doc(boostDocId);

    const now        = firebase.firestore.Timestamp.now();
    const nowMs      = Date.now();
    const durationMs = duration * 24 * 60 * 60 * 1000;
    const expiryTime = firebase.firestore.Timestamp.fromMillis(nowMs + durationMs);

    try {
      await db.runTransaction(async (tx) => {
        // Idempotency — skip if paymentId already processed
        const txSnap = await tx.get(txRef);
        if (txSnap.exists) return;

        const boostSnap = await tx.get(boostRef);

        tx.set(txRef, {
          ...(isSelfBoost ? { managerID } : { athleteId }),
          userId,
          featureType,
          duration,
          pricePerDay:   pricePerDay  || 0,
          totalAmount:   totalAmount  || 0,
          paymentId,
          paymentMethod: paymentMethod || "paypal",
          periodStart:   now,
          periodEnd:     expiryTime,
          createdAt:     now,
          ...(opinion ? { opinion } : {}),
        });

        // ── boost doc (create or update) ────────────────────────────────────
        if (boostSnap.exists) {
          const data = boostSnap.data();
          const currentExpiry = data.expiryTime?.seconds
            ? data.expiryTime.seconds * 1000
            : data.expiryTime?.toMillis?.() ?? nowMs;
          const baseTime = currentExpiry > nowMs ? currentExpiry : nowMs;
          const newExpiry = firebase.firestore.Timestamp.fromMillis(baseTime + durationMs);
          const boostedBy = data.boostedBy || [];
          if (!boostedBy.includes(userId)) boostedBy.push(userId);

          tx.update(boostRef, {
            totalBoosts:      firebase.firestore.FieldValue.increment(1),
            boostedBy,
            expiryTime:       newExpiry,
            lastBoostTime:    now,
            totalDuration:    (data.totalDuration || 0) + duration,
            totalAmountSpent: firebase.firestore.FieldValue.increment(totalAmount || 0),
            featureType,
            name:  athleteName,
            email: athleteEmail,
            ...(isSelfBoost ? { managerID } : { athleteId }),
          });
        } else {
          tx.set(boostRef, {
            ...(isSelfBoost ? { managerID } : { athleteId }),
            name:  athleteName,
            email: athleteEmail,
            totalBoosts:      1,
            boostedBy:        [userId],
            expiryTime,
            lastBoostTime:    now,
            totalDuration:    duration,
            totalAmountSpent: totalAmount || 0,
            featureType,
          });
        }
      });

      if (opinion) {
        await boostRef.collection("opinions").doc(paymentId).set({
          opinion,
          fanName:   fanName || "Anonymous",
          userId,
          paymentId,
          createdAt: now,
          expiryTime,
        });
      }
    } catch (error) {
      toast.error("Failed to save boost record.");
    }
  };

export const fetchOpinions = (boostDocId, fallbackOpinion) => async (dispatch) => {
  dispatch({ type: "SET_OPINIONS_LOADING", payload: true });
  try {
    const now = firebase.firestore.Timestamp.now();
    const snapshot = await firebase.firestore()
      .collection("boosts")
      .doc(boostDocId)
      .collection("opinions")
      .where("expiryTime", ">", now)
      .orderBy("expiryTime", "asc")
      .get();

    let opinions = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    // Fallback for legacy records that only have latestOpinion on the parent doc
    if (opinions.length === 0 && fallbackOpinion) {
      opinions = [{
        id: "legacy",
        opinion: fallbackOpinion,
        fanName: "Fan",
        expiryTime: null,
      }];
    }

    dispatch({ type: "SET_OPINIONS", payload: opinions });
  } catch (error) {
    dispatch({ type: "SET_OPINIONS", payload: [] });
  } finally {
    dispatch({ type: "SET_OPINIONS_LOADING", payload: false });
  }
};

export const clearOpinions = () => ({ type: "CLEAR_OPINIONS" });

// ─── Fetch user profile from Firestore users collection ──────────────────────
export const fetchBoostUserProfile = (uid) => async (dispatch) => {
  dispatch({ type: "SET_PROFILE_LOADING", payload: true });
  try {
    const doc = await firebase.firestore().collection("users").doc(uid).get();
    if (doc.exists) {
      dispatch({ type: "SET_USER_PROFILE", payload: { id: doc.id, ...doc.data() } });
    } else {
      dispatch({ type: "SET_USER_PROFILE", payload: null });
    }
  } catch (error) {
    dispatch({ type: "SET_USER_PROFILE", payload: null });
  } finally {
    dispatch({ type: "SET_PROFILE_LOADING", payload: false });
  }
};

export const clearBoostProfile = () => ({ type: "CLEAR_USER_PROFILE" });

// ─── Shared helper: enrich raw boost docs with user profile data ──────────────
const enrichBoostDocs = async (docs) => {
  const allEntries = docs.map((doc) => ({ id: doc.id, ...doc.data() }));

  const uniqueUids = [...new Set(
    allEntries
      .map((e) => e.athleteId || e.managerID || e.id?.split("_")[0])
      .filter(Boolean)
  )];

  let userMap = {};
  try {
    const chunkSize = 10; // Firestore 'in' query max limit
    for (let i = 0; i < uniqueUids.length; i += chunkSize) {
      const chunk = uniqueUids.slice(i, i + chunkSize);
      const usersSnap = await firebase.firestore()
        .collection("users")
        .where(firebase.firestore.FieldPath.documentId(), "in", chunk)
        .get();
      usersSnap.docs.forEach((doc) => {
        userMap[doc.id] = doc.data();
      });
    }
  } catch (err) {}

  return allEntries.map((e) => {
    const isManagerEntry = e.featureType === "selfBoost" || e.featureType === "promoteManager";
    const uid = isManagerEntry
      ? (e.managerID || e.athleteId || e.id?.split("_")[0])
      : (e.athleteId || e.id?.split("_")[0]);
    const userData = uid ? userMap[uid] : null;

    return {
      ...e,
      userProfile: userData || null,
      userPhoto:   userData?.player_image?.url || userData?.photo || null,
      ...(isManagerEntry
        ? { managerID: userData?.managerID || null }
        : { athleteID: userData?.athleteID || null }
      ),
    };
  });
};

const PAGE_SIZE = 5;

// featureTypes per reduxKey
const KEY_FEATURE_TYPES = {
  boost:    ["boost"],
  shoutout: ["shoutout"],
  manager:  ["promoteManager", "selfBoost"],
};

// ─── Sort entries by totalAmountSpent desc, then lastBoostTime desc ───────────
const sortByAmount = (entries) =>
  [...entries].sort((a, b) => {
    const amountDiff = (b.totalAmountSpent || 0) - (a.totalAmountSpent || 0);
    if (amountDiff !== 0) return amountDiff;
    // tie-break: most recently boosted first
    const aTime = a.lastBoostTime?.toMillis?.() ?? a.lastBoostTime?.seconds * 1000 ?? 0;
    const bTime = b.lastBoostTime?.toMillis?.() ?? b.lastBoostTime?.seconds * 1000 ?? 0;
    return bTime - aTime;
  });

// ─── Build a Firestore query for a given tab key ──────────────────────────────
// Firestore requires the first orderBy to match the range-filter field (expiryTime),
// so we fetch all active docs and sort by totalAmountSpent client-side.
const buildTabQuery = (key, lastDoc) => {
  const now = firebase.firestore.Timestamp.now();
  const featureTypes = KEY_FEATURE_TYPES[key];

  let query = firebase.firestore()
    .collection("boosts")
    .where("featureType", "in", featureTypes)
    .where("expiryTime", ">", now)
    .orderBy("expiryTime")   // required by Firestore when range-filtering on expiryTime
    .limit(PAGE_SIZE);

  if (lastDoc) query = query.startAfter(lastDoc);
  return query;
};

// ─── Load first page for a tab (resets that tab's state) ─────────────────────
export const loadBulletinBoardTab = (key) => async (dispatch) => {
  dispatch({ type: "RESET_TAB", payload: { key } });
  dispatch({ type: "SET_BOOST_LOADING", payload: true });

  try {
    const snapshot = await buildTabQuery(key, null).get();
    const enriched  = await enrichBoostDocs(snapshot.docs);
    const sorted    = sortByAmount(enriched);
    const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    const hasMore    = snapshot.docs.length === PAGE_SIZE;

    dispatch({ type: "SET_TAB_ENTRIES",  payload: { key, entries: sorted } });
    dispatch({ type: "SET_TAB_LAST_DOC", payload: { key, lastDoc: newLastDoc } });
    dispatch({ type: "SET_TAB_HAS_MORE", payload: { key, hasMore } });
  } catch (error) {
    console.error("loadBulletinBoardTab error:", error);
  } finally {
    dispatch({ type: "SET_BOOST_LOADING", payload: false });
  }
};

// ─── Load next page for a tab (appends to existing entries) ──────────────────
export const loadMoreBulletinBoard = (key) => async (dispatch, getState) => {
  const lastDoc = getState().boost.tabs[key]?.lastDoc;

  dispatch({ type: "SET_BOOST_LOADING_MORE", payload: true });

  try {
    const snapshot = await buildTabQuery(key, lastDoc).get();
    const enriched  = await enrichBoostDocs(snapshot.docs);
    const sorted    = sortByAmount(enriched);
    const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    const hasMore    = snapshot.docs.length === PAGE_SIZE;

    dispatch({ type: "APPEND_TAB_ENTRIES", payload: { key, entries: sorted } });
    dispatch({ type: "SET_TAB_LAST_DOC",   payload: { key, lastDoc: newLastDoc } });
    dispatch({ type: "SET_TAB_HAS_MORE",   payload: { key, hasMore } });
  } catch (error) {
    console.error("loadMoreBulletinBoard error:", error);
  } finally {
    dispatch({ type: "SET_BOOST_LOADING_MORE", payload: false });
  }
};
